// @ts-nocheck


import { createAction, createSlice } from '@reduxjs/toolkit';
import type { Dispatch } from 'redux';
import cloneDeep from 'lodash/cloneDeep';
import set from 'lodash/set';
import notification from 'antd/lib/notification';
import isEqual from 'lodash/isEqual';

import type { Vehicle, FuelMeasurement } from './../lib/types';
import {
  vehicleApi,
  batteryApi,
  tireApi,
  optionalEquipmentApi,
  attachedEquipmentApi,
  locationApi,
  fuelCardApi,
  fuelMultiplierApi,
  fuelMeasurementApi,
  osagoApi,
} from './../lib/api';
import { vehicleStatusEnum } from './../lib/enum';
import type { AppState } from './redux';

type VehicleThunkDispatch = Dispatch & {
  <Result>(
    thunk: (dispatch: Dispatch, getState: () => AppState) => Result
  ): Result;
};

export const FETCH_VEHICLE = 'vehicles/vehicle/fetch';
export const UPDATE_VEHICLE = 'vehicles/vehicles/update';
export const CLEAN_VEHICLE = 'vehicles/vehicle/clean';

export type VehicleState = Vehicle | null;

const initialState: VehicleState = null;

const fetchVehicleState = createAction<Vehicle>(FETCH_VEHICLE);
const updateVehicleState = createAction<Vehicle>(UPDATE_VEHICLE);
const cleanVehicleState = createAction(CLEAN_VEHICLE);

const vehicleSlice = createSlice({
  name: 'vehicles/vehicle',
  initialState: initialState as VehicleState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(fetchVehicleState, (_state, { payload }) => payload)
      .addCase(cleanVehicleState, () => initialState)
      .addCase(updateVehicleState, (_state, { payload }) => payload);
  }
});

export const vehicleActions = {
  fetch: fetchVehicleState,
  clean: cleanVehicleState,
  update: updateVehicleState
};

const getErrorMessage = (error: unknown): string =>
  error instanceof Error ? error.message : String(error);

export const addVehicle =
  (vehicle: Vehicle) =>
  async (dispatch: Dispatch): Promise<void> => {
    const { lastTo1Date, lastTo2Date } = vehicle;
    let added = await vehicleApi.addVehicle(vehicle);
    let hasErrorFill = false;
    try {
      await vehicleApi.fillNextToDate({ ...added, lastTo1Date, lastTo2Date });
    } catch (error) {
      hasErrorFill = true;
      notification.error({
        message: 'Ошибка',
        description: getErrorMessage(error),
      });
    }
    try {
      const osago = await osagoApi.addOsago({
        ...vehicle.osago,
        vehicleId: added.id,
      });
      hasErrorFill &&
        osago.id &&
        (added = await vehicleApi.updateVehicle({
          ...added,
          lastTo1Date,
          lastTo2Date,
          osago,
          osagoId: osago.id,
        }));
    } catch (error) {
      notification.error({
        message: 'Ошибка',
        description: getErrorMessage(error),
      });
    }
    dispatch(vehicleActions.update(added));
  };

const addOsago = async (vehicle: Vehicle) => {
  const osago = await osagoApi.addOsago({
    ...vehicle.osago,
    vehicleId: vehicle.id,
  });
  const updated = await vehicleApi.updateVehicle({
    ...vehicle,
    osago,
    osagoId: osago.id,
  });
  return { vehicle: updated, osago };
};

export const updateVehicle =
  (vehicle: Vehicle) =>
  async (dispatch: any, getState: () => AppState): Promise<void> => {
    const { lastTo1Date, lastTo2Date } = vehicle;
    let updated = await vehicleApi.updateVehicle(vehicle);
    await vehicleApi.fillNextToDate({ ...updated, lastTo1Date, lastTo2Date });
    if (!isEqual(vehicle.osago, updated.osago)) {
      const id = vehicle?.osago?.id ?? vehicle.osagoId ?? null;
      if (id) {
        try {
          await osagoApi.updateOsago({
            ...vehicle.osago,
            id: vehicle?.osago?.id ?? vehicle.osagoId,
          });
        } catch (error) {
          if (getErrorMessage(error) !== 'Osago не найден') throw error;

          updated = (
            await addOsago({
              ...updated,
              osago: { ...vehicle.osago },
            })
          ).vehicle;
        }
      } else if (vehicle.osago) {
        updated = (
          await addOsago({
            ...updated,
            osago: { ...vehicle.osago },
          })
        ).vehicle;
      }
    }
    const { status: prevStatus } = getState().vehicle ?? {};
    /**
     * Сбрасываем статус на Черновик, только если не поменялся статус,
     * а другие поля поменялись
     * TODO: Продумать логику с тем, что если мы поменяем и статус и какие-то поля
     * иначе это вводит в конфуз
     */
    if (updated && updated.status === prevStatus) {
      dispatch(resetVehicleStatus(updated.id));
    } else {
      dispatch(fetchVehicle(vehicle.id));
    }
  };

// Загрузить ТС по id
let latestVehicleFetchRequest = 0;

export const fetchVehicle =
  (id: number) =>
  async (dispatch: any, getState: () => AppState): Promise<void> => {
    const requestId = ++latestVehicleFetchRequest;
    const isCurrentRequest = () => requestId === latestVehicleFetchRequest;
    const vehicle: Vehicle = await vehicleApi.fetchVehicle(id);
    if (!isCurrentRequest()) return;

    if (vehicle) {
      // местоположение
      if (Number(vehicle.locationId) > 0) {
        vehicle.location = await locationApi.fetchLocation(vehicle.locationId);
        if (!isCurrentRequest()) return;
      }
      vehicle.fuelMeasurements = await fetchVehicleFuelMeasurements(vehicle.id);
      if (!isCurrentRequest()) return;
      const oldVehicle = cloneDeep(getState().vehicle);
      /**
       * Чтобы не стирать уже загруженные данные
       * (чтобы страничка с оборудованием, например, не моргала при контрольном замере)
       */
      if (oldVehicle && oldVehicle.id === id) {
        vehicle.equipments = oldVehicle.equipments;
        vehicle.fuelCard = oldVehicle.fuelCard;
        vehicle.fuelMultipliers = oldVehicle.fuelMultipliers;
        vehicle.fuelsAndOils = oldVehicle.fuelsAndOils;
      }

      dispatch(vehicleActions.fetch(vehicle));
      dispatch(setVehicleBatteries(id));
      dispatch(setVehicleTires(id));
      dispatch(setVehicleOptionalEquipments(id));
      dispatch(setVehicleAttachedEquipments(id));
      dispatch(setVehicleFuelCard(id));
      dispatch(setVehicleFuelMultipliers(id));
    } else {
      return Promise.reject(new Error(`ТС с идентификатором ${id} не найдено`));
    }
  };

// Очищаем текущее ТС
export const cleanVehicle = () => (dispatch: Dispatch) => {
  dispatch(vehicleActions.clean());
};

export const setVehicleBatteries =
  (vehicleId: number) =>
  async (dispatch: any, getState: () => AppState): Promise<void> => {
    const batteries = await batteryApi.fetchVehicleBatteries(vehicleId);
    if (batteries) {
      const vehicle = cloneDeep(getState().vehicle);
      if (vehicle && vehicle.id === vehicleId) {
        /**
         * Может быть так, что vehicle.equipments === null
         * Метод set автоматически создаст объект equipments, если него нет
         */
        set(vehicle, 'equipments.batteries', batteries);
        dispatch(vehicleActions.fetch(vehicle));
      }
    }
  };

export const setVehicleTires =
  (vehicleId: number) =>
  async (dispatch: any, getState: () => AppState): Promise<void> => {
    const tires = await tireApi.fetchVehicleTires(vehicleId);
    if (tires) {
      const vehicle = cloneDeep(getState().vehicle);
      if (vehicle && vehicle.id === vehicleId) {
        /**
         * Может быть так, что vehicle.equipments === null
         * Метод set автоматически создаст объект equipments, если него нет
         */
        set(vehicle, 'equipments.tires', tires);
        dispatch(vehicleActions.fetch(vehicle));
      }
    }
  };

const fetchVehicleFuelMeasurements = async (
  vehicleId: number
): Promise<Array<FuelMeasurement>> => {
  try {
    return await fuelMeasurementApi.fetchVehicleFuelMeasurements(vehicleId);
  } catch (err) {
    notification.error({
      message: 'Ошибка',
      description: getErrorMessage(err),
    });
    return [];
  }
};

export const setVehicleOptionalEquipments =
  (vehicleId: number) =>
  async (dispatch: any, getState: () => AppState): Promise<void> => {
    const optionalEquipment =
      await optionalEquipmentApi.fetchVehicleOptionalEquipments(vehicleId);
    if (optionalEquipment) {
      const vehicle = cloneDeep(getState().vehicle);
      if (vehicle && vehicle.id === vehicleId) {
        /**
         * Может быть так, что vehicle.equipments === null
         * Метод set автоматически создаст объект equipments, если него нет
         */
        set(vehicle, 'equipments.optional', optionalEquipment);
        dispatch(vehicleActions.fetch(vehicle));
      }
    }
  };

export const setVehicleAttachedEquipments =
  (vehicleId: number) =>
  async (dispatch: any, getState: () => AppState): Promise<void> => {
    const attachedEquipment =
      await attachedEquipmentApi.fetchVehicleAttachedEquipments(vehicleId);
    if (attachedEquipment) {
      const vehicle = cloneDeep(getState().vehicle);
      if (vehicle && vehicle.id === vehicleId) {
        /**
         * Может быть так, что vehicle.equipments === null
         * Метод set автоматически создаст объект equipments, если него нет
         */
        set(vehicle, 'equipments.attached', attachedEquipment);
        dispatch(vehicleActions.fetch(vehicle));
      }
    }
  };

export const setVehicleFuelCard =
  (vehicleId: number) =>
  async (dispatch: Dispatch, getState: () => AppState): Promise<void> => {
    const fuelCards = await fuelCardApi.fetchVehicleFuelCards(vehicleId);
    if (fuelCards) {
      const vehicle = cloneDeep(getState().vehicle);
      if (vehicle && vehicle.id === vehicleId) {
        vehicle.fuelCard = fuelCards[0];
        dispatch(vehicleActions.fetch(vehicle));
      }
    }
  };

export const resetVehicleStatus =
  (vehicleId: number | null) =>
  async (dispatch: any, getState: () => AppState) => {
    try {
      const parsedVehicleId = Number(vehicleId);
      if (parsedVehicleId > 0) {
        const vehicle = await vehicleApi.fetchVehicle(parsedVehicleId);
        if (vehicle.status !== vehicleStatusEnum.draft) {
          await vehicleApi.updateVehicle({
            ...vehicle,
            status: vehicleStatusEnum.draft as Vehicle['status'],
          });
          const clonedVehicle = cloneDeep(getState().vehicle);
          if (vehicle && vehicle.id === vehicleId) {
            dispatch(
              vehicleActions.fetch({
                ...clonedVehicle,
                status: vehicleStatusEnum.draft as Vehicle['status']
              })
            );
          }
        }
      }
    } catch (err) {
      notification.error({
        message: 'Произошла ошибка при смене статуса ТС',
        description: getErrorMessage(err),
      });
    }
  };

export const setVehicleFuelMultipliers =
  (vehicleId: number) =>
  async (dispatch: Dispatch, getState: () => AppState): Promise<void> => {
    const fuelMultipliers = await fuelMultiplierApi.fetchVehicleFuelMultipliers(
      vehicleId
    );
    if (fuelMultipliers) {
      const vehicle = cloneDeep(getState().vehicle);
      if (vehicle && vehicle.id === vehicleId) {
        vehicle.fuelMultipliers = fuelMultipliers;
        dispatch(vehicleActions.fetch(vehicle));
      }
    }
  };

export default vehicleSlice.reducer;
